import { CREDENTIAL_TRANSFER_ALGORITHM, type AgentCredentialPublicKey, type EncryptedCredential } from "./types";

type NodeBufferLike = { length: number; [index: number]: number; toString: (encoding: string) => string };
type NodeBufferFactory = {
  from: (input: string | Uint8Array<ArrayBufferLike>, encoding?: string) => NodeBufferLike;
};

function nodeBuffer(): NodeBufferFactory | undefined {
  return (globalThis as unknown as { Buffer?: NodeBufferFactory }).Buffer;
}

export function bytesToBase64(data: ArrayBuffer | Uint8Array<ArrayBufferLike>): string {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const buffer = nodeBuffer();
  if (buffer) return buffer.from(bytes).toString("base64");

  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const buffer = nodeBuffer();
  if (buffer) {
    const decoded = buffer.from(value, "base64");
    const bytes = new Uint8Array(decoded.length);
    for (let i = 0; i < decoded.length; i++) bytes[i] = decoded[i];
    return bytes;
  }

  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function copyToArrayBuffer(bytes: Uint8Array<ArrayBufferLike>): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) throw new Error("WebCrypto indisponible pour chiffrer le secret.");
  return subtle;
}

function validateCredentialKey(key: AgentCredentialPublicKey | null | undefined): AgentCredentialPublicKey {
  if (!key?.kid || !key.spki || key.alg !== CREDENTIAL_TRANSFER_ALGORITHM) {
    throw new Error("Clé publique agent indisponible pour chiffrer le secret.");
  }
  if (key.expiresAt) {
    const expiresAt = Date.parse(key.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
      throw new Error("Clé publique agent expirée, reconnecter l'agent.");
    }
  }
  return key;
}

export async function encryptCredentialPassword(
  key: AgentCredentialPublicKey | null | undefined,
  password: string,
): Promise<EncryptedCredential> {
  const credentialKey = validateCredentialKey(key);
  const subtle = getSubtle();
  const rsaKey = await subtle.importKey(
    "spki",
    copyToArrayBuffer(base64ToBytes(credentialKey.spki)),
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"],
  );
  const aesKey = await subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt"]);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(password);
  const ciphertext = await subtle.encrypt({ name: "AES-GCM", iv }, aesKey, plaintext);
  const rawAesKey = await subtle.exportKey("raw", aesKey);
  const encryptedKey = await subtle.encrypt({ name: "RSA-OAEP" }, rsaKey, rawAesKey);

  plaintext.fill(0);

  return {
    v: 1,
    alg: CREDENTIAL_TRANSFER_ALGORITHM,
    kid: credentialKey.kid,
    encryptedKey: bytesToBase64(encryptedKey),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(ciphertext),
    encoding: "base64",
  };
}
